import { useEffect, useState } from "react";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Timer } from "@phosphor-icons/react";

export const SLA_OPTIONS = [2, 4, 6, 8];
export const TRADES = ["Plumber", "Electrician", "Heating engineer (Gas Safe)", "Locksmith", "Glazier", "Roofer", "Drainage", "Handyman / make-safe", "Lift engineer", "Pest control", "Other"];

/** 2 / 4 / 6 / 8 hour chips. */
export function SlaPicker({ value, onChange, testId = "ooh-sla" }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {SLA_OPTIONS.map((h) => (
        <button key={h} type="button" onClick={() => onChange(h)} className={`px-3 py-1.5 rounded-md border text-sm font-semibold transition-colors ${value === h ? "border-red-500 bg-red-600 text-white" : "border-border hover:bg-muted"}`} data-testid={`${testId}-${h}`}>
          {h}h
        </button>
      ))}
    </div>
  );
}

const fmt = (secs) => {
  const s = Math.abs(secs);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
};

/** Live countdown to the SLA deadline; goes red when breached, green once the contractor is on site. */
export function SlaCountdown({ sla, compact = false }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 30000); return () => clearInterval(t); }, []);
  if (!sla?.due_at) return null;
  if (sla.on_site_at) {
    return <span className={`inline-flex items-center gap-1 font-semibold ${sla.met ? "text-emerald-700" : "text-amber-700"}`} data-testid="ooh-sla-countdown"><Timer size={compact ? 11 : 13} weight="fill" /> {sla.met ? "SLA met" : "SLA missed"} · on site</span>;
  }
  const remaining = Math.round((new Date(sla.due_at) - Date.now()) / 1000);
  const breached = remaining < 0;
  const warn = !breached && remaining < 1800;
  const cls = breached ? "text-white bg-red-600 animate-pulse" : warn ? "text-amber-900 bg-amber-200" : "text-red-800 bg-red-100 dark:bg-red-900/40 dark:text-red-200";
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-semibold tabular-nums ${compact ? "text-[10px]" : "text-xs"} ${cls}`} data-testid="ooh-sla-countdown" title={`${sla.hours}h SLA · attend by ${new Date(sla.due_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" })}`}>
      <Timer size={compact ? 11 : 13} weight="fill" /> {breached ? `SLA OVERDUE ${fmt(remaining)}` : `SLA ${fmt(remaining)} left`}
    </span>
  );
}

/** Name / company / phone / email / trade for a contractor who isn't on the supplier list yet. */
export function ManualContractorFields({ value, onChange, prefix = "ooh-manual" }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid={`${prefix}-fields`}>
      <Input value={value.name} onChange={(e) => set("name", e.target.value)} placeholder="Contact name *" data-testid={`${prefix}-name`} />
      <Input value={value.company} onChange={(e) => set("company", e.target.value)} placeholder="Company (optional)" data-testid={`${prefix}-company`} />
      <Input value={value.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone *" type="tel" data-testid={`${prefix}-phone`} />
      <Input value={value.email} onChange={(e) => set("email", e.target.value)} placeholder="Email (we'll send them the job details)" type="email" data-testid={`${prefix}-email`} />
      <select value={value.trade} onChange={(e) => set("trade", e.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid={`${prefix}-trade`}>
        <option value="">Trade…</option>
        {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <label className="flex items-center gap-2 text-xs">
        <Checkbox checked={!!value.save_as_supplier} onCheckedChange={(v) => set("save_as_supplier", !!v)} data-testid={`${prefix}-save`} /> Add them to the supplier list (flagged OOH-available)
      </label>
    </div>
  );
}

export const emptyManual = () => ({ name: "", company: "", phone: "", email: "", trade: "", save_as_supplier: false });
